import type {
  AnalyticsDashboard,
  UserAnalytics,
  FeatureAnalytics,
  RevenueAnalytics,
  AIUsageAnalytics,
  SystemAnalytics,
  InsightRecommendation,
  AdminExtraAnalytics
} from '@my-roadtrip/types'
import { isString, isNonEmptyString } from '../common/utils/type-guards.js'
import { db } from '../core/database/firebase.js'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('AnalyticsService')
const ANALYTICS_USE_MOCKS = process.env.ANALYTICS_USE_MOCKS === 'true' || process.env.NODE_ENV === 'test'
const DEFAULT_FEATURES = ['ai-planner', 'poi-discovery', 'route-optimization', 'collaboration', 'map-view']

class AnalyticsService {
  private useMocks(): boolean {
    return ANALYTICS_USE_MOCKS
  }

  private emptyUserAnalytics(period: UserAnalytics['period']): UserAnalytics {
    return {
      period,
      totalUsers: 0,
      activeUsers: 0,
      newUsers: 0,
      churned: 0,
      retention: 0,
      engagement: {
        dailyActiveUsers: 0,
        weeklyActiveUsers: 0,
        monthlyActiveUsers: 0,
        averageSessionDuration: 0,
        averageSessionsPerUser: 0
      },
      byPlan: {},
      growth: {
        userGrowth: 0,
        revenueGrowth: 0,
        trend: 'stable'
      },
      topCountries: []
    }
  }

  private mockUserAnalytics(period: UserAnalytics['period']): UserAnalytics {
    return {
      period,
      totalUsers: 1240,
      activeUsers: 520,
      newUsers: 84,
      churned: 18,
      retention: 78.5,
      engagement: {
        dailyActiveUsers: 310,
        weeklyActiveUsers: 640,
        monthlyActiveUsers: 820,
        averageSessionDuration: 14,
        averageSessionsPerUser: 3.2
      },
      byPlan: {
        free: { count: 720, revenue: 0, churnRate: 14 },
        premium: { count: 360, revenue: 36000, churnRate: 7 },
        pro: { count: 160, revenue: 52000, churnRate: 4 }
      },
      growth: {
        userGrowth: 6.4,
        revenueGrowth: 8.1,
        trend: 'up'
      },
      topCountries: [
        { country: 'France', count: 520, percentage: 41.9 },
        { country: 'Belgium', count: 180, percentage: 14.5 },
        { country: 'Canada', count: 140, percentage: 11.3 }
      ]
    }
  }

  private emptyRevenueAnalytics(period: RevenueAnalytics['period']): RevenueAnalytics {
    return {
      period,
      total: 0,
      recurring: 0,
      oneTime: 0,
      byPlan: {},
      churn: { rate: 0, count: 0, revenue: 0 },
      ltv: { average: 0, byPlan: {} },
      forecast: { nextMonth: 0, nextQuarter: 0, confidence: 0 },
      paymentMethods: {}
    }
  }

  private mockRevenueAnalytics(period: RevenueAnalytics['period']): RevenueAnalytics {
    return {
      period,
      total: 125000,
      recurring: 98000,
      oneTime: 27000,
      byPlan: {
        basic: { revenue: 45000, subscribers: 150, averageRevenuePerUser: 300 },
        premium: { revenue: 53000, subscribers: 75, averageRevenuePerUser: 706 },
        pro: { revenue: 27000, subscribers: 32, averageRevenuePerUser: 844 }
      },
      churn: {
        rate: 6.5,
        count: 12,
        revenue: 8500
      },
      ltv: {
        average: 24000,
        byPlan: {
          basic: 18000,
          premium: 42000,
          pro: 52000
        }
      },
      forecast: {
        nextMonth: 135000,
        nextQuarter: 420000,
        confidence: 0.75
      },
      paymentMethods: {
        card: { count: 200, amount: 100000 },
        sepa: { count: 25, amount: 25000 }
      }
    }
  }

  private emptyFeatureAnalytics(): FeatureAnalytics[] {
    return []
  }

  private mockFeatureAnalytics(): FeatureAnalytics[] {
    return DEFAULT_FEATURES.map((feature, index) => ({
      feature,
      usage: {
        total: 1500 + index * 120,
        unique: 180 + index * 10,
        averagePerUser: 4 + index * 0.5
      },
      engagement: {
        activeUsers: 120 + index * 8,
        adoptionRate: 35 + index * 3,
        stickiness: 0.42
      },
      performance: {
        averageLoadTime: 420 + index * 15,
        errorRate: 0.8 + index * 0.1,
        successRate: 97.5
      },
      trend: {
        current: 320 + index * 20,
        previous: 280 + index * 15,
        change: 8.5
      },
      topUsers: []
    }))
  }

  private emptyAIUsageAnalytics(period: AIUsageAnalytics['period']): AIUsageAnalytics {
    return {
      period,
      totalRequests: 0,
      totalTokens: 0,
      totalCost: 0,
      byModel: {},
      byFeature: {},
      performance: {
        averageResponseTime: 0,
        errorRate: 0,
        retryRate: 0
      },
      quality: {
        userSatisfaction: undefined,
        thumbsUp: 0,
        thumbsDown: 0
      },
      topPrompts: []
    }
  }

  private mockAIUsageAnalytics(period: AIUsageAnalytics['period']): AIUsageAnalytics {
    return {
      period,
      totalRequests: 8450,
      totalTokens: 2_840_000,
      totalCost: 4250,
      byModel: {
        'gpt-4-turbo': {
          requests: 3200,
          tokens: 1_280_000,
          cost: 2560,
          averageTokensPerRequest: 400
        },
        'gpt-3.5-turbo': {
          requests: 5250,
          tokens: 1_560_000,
          cost: 1690,
          averageTokensPerRequest: 297
        }
      },
      byFeature: {
        'ai-planner': { requests: 4500, tokens: 1_800_000, cost: 3000 },
        'poi-suggestions': { requests: 2800, tokens: 700_000, cost: 900 },
        'chat-assistant': { requests: 1150, tokens: 340_000, cost: 350 }
      },
      performance: {
        averageResponseTime: 2400,
        errorRate: 1.2,
        retryRate: 0.8
      },
      quality: {
        userSatisfaction: 4.3,
        thumbsUp: 320,
        thumbsDown: 45
      },
      topPrompts: []
    }
  }

  /**
   * Additional admin-only analytics (activation, funnels, reliability)
   */
  async getAdminExtras(): Promise<AdminExtraAnalytics> {
    // Basic fallbacks if no data
    const empty: AdminExtraAnalytics = {
      activation: { d1: 0, d7: 0, sample: 0 },
      plannerFunnel: { created: 0, completed: 0, shared: 0, completionRate: 0, shareRate: 0 },
      offlineRatio: { offlineOps: 0, onlineOps: 0, offlineShare: 0 },
      aiLatencyMs: { avg: 0, p95: 0 },
      routingErrors: { osrmFailureRate: 0, mapboxFailureRate: 0, totalRequests: 0 },
      returnToPlan: { views: 0, returns: 0, rate: 0 }
    }

    try {
      // Activation cohorts: user_activity
      const now = new Date()
      const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      const userActivity = await db
        .collection('user_activity')
        .where('timestamp', '>=', weekAgo)
        .get()
      const usersSeen = new Map<string, { first: number; last: number }>()
      for (const doc of userActivity.docs) {
        const data = doc.data() as Record<string, any>
        const userId = data.userId || data.uid
        const ts = data.timestamp ? new Date(data.timestamp).getTime() : 0
        if (!userId || !ts) continue
        const prev = usersSeen.get(userId) ?? { first: ts, last: ts }
        usersSeen.set(userId, { first: Math.min(prev.first, ts), last: Math.max(prev.last, ts) })
      }
      const sample = usersSeen.size
      let d1 = 0
      let d7 = 0
      for (const info of usersSeen.values()) {
        if (info.last - info.first >= 24 * 60 * 60 * 1000) d1++
        if (info.last - info.first >= 7 * 24 * 60 * 60 * 1000) d7++
      }

      // Planner funnel
      const plansSnapshot = await db.collection('plans').get()
      const created = plansSnapshot.size
      let completed = 0
      let shared = 0
      for (const planDoc of plansSnapshot.docs) {
        const data = planDoc.data() as Record<string, any>
        if (data.status === 'completed' || data.completed === true) completed++
        if (data.isPublic === true || (Array.isArray(data.sharedWith) && data.sharedWith.length > 0)) shared++
      }
      const completionRate = created > 0 ? (completed / created) * 100 : 0
      const shareRate = created > 0 ? (shared / created) * 100 : 0

      // Offline vs online from collection offline_ops (optional)
      let offlineOps = 0
      let onlineOps = 0
      try {
        const offlineSnap = await db.collection('offline_ops').limit(5000).get()
        offlineOps = offlineSnap.size
      } catch {
        /* ignore if collection absent */
      }
      // Approximate online ops = total stages writes
      const stagesSnap = await db.collection('stages').limit(5000).get()
      onlineOps = stagesSnap.size
      const offlineShare = offlineOps + onlineOps > 0 ? offlineOps / (offlineOps + onlineOps) : 0

      // AI latency: from collection ai_requests (optional)
      let aiAvg = 0
      let aiP95 = 0
      try {
        const aiSnap = await db.collection('ai_requests').limit(1000).get()
        const durations: number[] = []
        aiSnap.forEach((doc) => {
          const d = doc.data() as Record<string, any>
          if (typeof d.durationMs === 'number') durations.push(d.durationMs)
        })
        if (durations.length) {
          const sum = durations.reduce((a, b) => a + b, 0)
          aiAvg = sum / durations.length
          const sorted = durations.sort((a, b) => a - b)
          aiP95 = sorted[Math.floor(0.95 * (sorted.length - 1))]
        }
      } catch {
        /* ignore */
      }

      // Routing error rate: from admin_health_history checks? (requests.errorRate)
      let osrmFailureRate = 0
      let mapboxFailureRate = 0
      let totalRequests = 0
      try {
        const healthSnap = await db
          .collection('admin_health_history')
          .orderBy('timestamp', 'desc')
          .limit(200)
          .get()
        let osrmFails = 0
        let mapboxFails = 0
        let count = 0
        healthSnap.forEach((doc) => {
          const data = doc.data() as Record<string, any>
          count++
          if (data.checks?.osrm === false) osrmFails++
          if (data.checks?.mapbox === false) mapboxFails++
        })
        if (count > 0) {
          osrmFailureRate = (osrmFails / count) * 100
          mapboxFailureRate = (mapboxFails / count) * 100
          totalRequests = count
        }
      } catch {
        /* ignore */
      }

      // Return-to-plan after share: use shares collection + plan_views (if exists)
      let views = 0
      let returns = 0
      try {
        const shareSnap = await db.collection('shares').limit(1000).get()
        views = shareSnap.size
        // If we had plan_views, approximate by public plan hits stored as counter
        const planViewsSnap = await db.collection('plan_views').limit(1000).get()
        returns = planViewsSnap.size
      } catch {
        /* ignore */
      }
      const rate = views > 0 ? Math.min(100, (returns / views) * 100) : 0

      return {
        activation: { d1, d7, sample },
        plannerFunnel: { created, completed, shared, completionRate, shareRate },
        offlineRatio: { offlineOps, onlineOps, offlineShare },
        aiLatencyMs: { avg: aiAvg, p95: aiP95 },
        routingErrors: { osrmFailureRate, mapboxFailureRate, totalRequests },
        returnToPlan: { views, returns, rate }
      }
    } catch (error) {
      logger.error('getAdminExtras failed', { error })
      return empty
    }
  }

  private emptySystemAnalytics(period: SystemAnalytics['period']): SystemAnalytics {
    return {
      period,
      availability: 0,
      performance: {
        averageResponseTime: 0,
        p50: 0,
        p95: 0,
        p99: 0
      },
      errors: {
        total: 0,
        rate: 0,
        byType: {},
        topErrors: []
      },
      requests: {
        total: 0,
        successful: 0,
        failed: 0,
        byEndpoint: {}
      },
      cache: {
        hitRate: 0,
        hits: 0,
        misses: 0,
        evictions: 0
      },
      database: {
        reads: 0,
        writes: 0,
        deletes: 0,
        averageQueryTime: 0
      }
    }
  }

  private mockSystemAnalytics(period: SystemAnalytics['period']): SystemAnalytics {
    return {
      period,
      availability: 99.2,
      performance: {
        averageResponseTime: 245,
        p50: 180,
        p95: 520,
        p99: 890
      },
      errors: {
        total: 124,
        rate: 1.2,
        byType: {
          '500': 45,
          '503': 32,
          '429': 28,
          '400': 19
        },
        topErrors: [
          { error: 'OpenAI timeout', count: 32, lastOccurrence: new Date() },
          { error: 'Database connection pool exhausted', count: 18, lastOccurrence: new Date() },
          { error: 'Rate limit exceeded', count: 15, lastOccurrence: new Date() }
        ]
      },
      requests: {
        total: 124_580,
        successful: 123_456,
        failed: 1_124,
        byEndpoint: {
          '/api/planner/generate': { count: 8500, averageResponseTime: 3200, errorRate: 1.5 },
          '/api/search/poi': { count: 24_000, averageResponseTime: 450, errorRate: 0.8 },
          '/api/maps/route': { count: 18_000, averageResponseTime: 680, errorRate: 1.2 }
        }
      },
      cache: {
        hitRate: 78.5,
        hits: 42_000,
        misses: 11_500,
        evictions: 850
      },
      database: {
        reads: 280_000,
        writes: 42_000,
        deletes: 1_200,
        averageQueryTime: 12
      }
    }
  }

  /**
   * Get complete analytics dashboard
   */
  async getDashboard(period: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'): Promise<AnalyticsDashboard> {
    const [users, revenue, features, aiUsage, system] = await Promise.all([
      this.getUserAnalytics(period),
      this.getRevenueAnalytics(period),
      this.getFeatureAnalytics(period),
      this.getAIUsageAnalytics(period === 'quarter' || period === 'year' ? 'month' : period),
      this.getSystemAnalytics(period === 'quarter' || period === 'year' ? 'month' : period)
    ])

    const insights = await this.generateInsights({ users, revenue, features, aiUsage, system })

    const kpis = [
      {
        name: 'Active Users',
        value: users.activeUsers,
        target: users.totalUsers * 0.4, // 40% activation target
        trend: users.growth.trend,
        change: users.growth.userGrowth
      },
      {
        name: 'MRR',
        value: revenue.recurring,
        target: revenue.recurring * 1.1, // 10% growth target
        trend: revenue.forecast.nextMonth > revenue.recurring ? 'up' as const : 'down' as const,
        change: revenue.churn.rate
      },
      {
        name: 'Retention',
        value: users.retention,
        target: 80, // 80% retention target
        trend: users.retention >= 75 ? 'up' as const : 'down' as const,
        change: 0
      },
      {
        name: 'Uptime',
        value: system.availability,
        target: 99.9,
        trend: system.availability >= 99 ? 'up' as const : 'down' as const,
        change: 0
      }
    ]

    return {
      period,
      users,
      revenue,
      features,
      aiUsage,
      system,
      insights,
      kpis
    }
  }

  /**
   * Get user analytics
   */
  async getUserAnalytics(period: 'day' | 'week' | 'month' | 'quarter' | 'year'): Promise<UserAnalytics> {
    if (this.useMocks()) return this.mockUserAnalytics(period)

    const { startDate, endDate } = this.getDateRange(period)

    try {
      const usersSnapshot = await db.collection('users').get()
      const totalUsers = usersSnapshot.size
      const planStats: Record<string, { count: number; revenue: number; churnRate: number }> = {}
      const countryCounts = new Map<string, number>()

      for (const doc of usersSnapshot.docs) {
        const data = doc.data() as Record<string, any>
        const planValue = typeof data.plan === 'string' ? data.plan : data.plan?.tier
        const planKey = planValue || 'free'
        if (!planStats[planKey]) {
          planStats[planKey] = { count: 0, revenue: 0, churnRate: 0 }
        }
        planStats[planKey].count += 1

        const country =
          data.country ||
          data.locale?.country ||
          data.profile?.country ||
          data.address?.country ||
          data.geo?.country
        if (country && isString(country)) {
          countryCounts.set(country, (countryCounts.get(country) ?? 0) + 1)
        }
      }

      const activitySnapshot = await db
        .collection('user_activity')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()

      const activeUserIds = new Set<string>()
      const dailyActive = new Set<string>()
      const weeklyActive = new Set<string>()
      let totalDurationMinutes = 0

      const now = new Date()
      const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

      for (const doc of activitySnapshot.docs) {
        const data = doc.data() as Record<string, any>
        const userId = data.userId || data.uid || data.user
        const timestampValue = data.timestamp
        const timestamp = timestampValue ? new Date(timestampValue) : null
        if (userId) activeUserIds.add(String(userId))
        if (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())) {
          if (timestamp >= dayAgo) dailyActive.add(String(userId))
          if (timestamp >= weekAgo) weeklyActive.add(String(userId))
        }
        const durationRaw = typeof data.duration === 'number' ? data.duration : data.durationSeconds
        if (typeof durationRaw === 'number') {
          totalDurationMinutes += durationRaw / 60
        }
      }

      const activeUsers = activeUserIds.size

      const newUsersSnapshot = await db
        .collection('users')
        .where('createdAt', '>=', startDate)
        .where('createdAt', '<=', endDate)
        .get()
      const newUsers = newUsersSnapshot.size

      const engagement = {
        dailyActiveUsers: dailyActive.size || activeUsers,
        weeklyActiveUsers: weeklyActive.size || activeUsers,
        monthlyActiveUsers: activeUsers,
        averageSessionDuration: activitySnapshot.size
          ? Math.round((totalDurationMinutes / activitySnapshot.size) * 10) / 10
          : 0,
        averageSessionsPerUser: activeUsers ? Math.round((activitySnapshot.size / activeUsers) * 10) / 10 : 0
      }

      const topCountries = Array.from(countryCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([country, count]) => ({
          country,
          count,
          percentage: totalUsers > 0 ? Math.round((count / totalUsers) * 1000) / 10 : 0
        }))

      return {
        period,
        totalUsers,
        activeUsers,
        newUsers,
        churned: 0,
        retention: totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 10_000) / 100 : 0,
        engagement,
        byPlan: planStats,
        growth: {
          userGrowth: totalUsers > 0 ? Math.round((newUsers / totalUsers) * 1000) / 10 : 0,
          revenueGrowth: 0,
          trend: newUsers > 0 ? 'up' : 'stable'
        },
        topCountries
      }
    } catch (error) {
      logger.error('[Analytics] Failed to compute user analytics', { error })
      return this.emptyUserAnalytics(period)
    }
  }

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(period: 'day' | 'week' | 'month' | 'quarter' | 'year'): Promise<RevenueAnalytics> {
    if (this.useMocks()) return this.mockRevenueAnalytics(period)

    const { startDate, endDate } = this.getDateRange(period)

    try {
      const plansSnapshot = await db.collection('billingPlans').get()
      const planMap = new Map<string, any>()
      for (const doc of plansSnapshot.docs) {
        planMap.set(doc.id, doc.data())
      }

      const usersSnapshot = await db.collection('users').get()
      const byPlan: RevenueAnalytics['byPlan'] = {}

      let recurring = 0
      let activeSubscribers = 0
      let churnCount = 0
      let churnRevenue = 0

      for (const doc of usersSnapshot.docs) {
        const data = doc.data() as Record<string, any>
        const subscription = data.subscription as Record<string, any> | undefined
        const planId: string =
          (subscription?.planId as string) ||
          (typeof data.plan === 'string' ? data.plan : data.plan?.id) ||
          'free'

        const plan = planMap.get(planId)
        const monthlyPrice = typeof plan?.monthlyPrice === 'number' ? plan.monthlyPrice : 0

        if (!byPlan[planId]) {
          byPlan[planId] = { revenue: 0, subscribers: 0, averageRevenuePerUser: 0 }
        }

        const status: string | null =
          typeof subscription?.status === 'string'
            ? subscription.status
            : typeof data.subscriptionStatus === 'string'
              ? data.subscriptionStatus
              : null

        if (status === 'active') {
          byPlan[planId].subscribers += 1
          byPlan[planId].revenue += monthlyPrice
          recurring += monthlyPrice
          activeSubscribers += 1
        }

        const updatedAtRaw = subscription?.updatedAt
        const updatedAt = updatedAtRaw ? new Date(updatedAtRaw) : null
        if (status === 'canceled' && updatedAt && updatedAt >= startDate && updatedAt <= endDate) {
          churnCount += 1
          churnRevenue += monthlyPrice
        }
      }

      for (const [planId, stats] of Object.entries(byPlan)) {
        if (stats.subscribers > 0) {
          stats.averageRevenuePerUser = Math.round((stats.revenue / stats.subscribers) * 100) / 100
        }
      }

      const total = recurring
      const churnRate = activeSubscribers > 0 ? Math.round((churnCount / activeSubscribers) * 10_000) / 100 : 0

      return {
        period,
        total,
        recurring,
        oneTime: 0,
        byPlan,
        churn: {
          rate: churnRate,
          count: churnCount,
          revenue: churnRevenue
        },
        ltv: {
          average: activeSubscribers ? Math.round((recurring / activeSubscribers) * 12) : 0,
          byPlan: Object.fromEntries(
            Object.entries(byPlan).map(([planId, stats]) => [
              planId,
              stats.subscribers ? Math.round((stats.revenue / stats.subscribers) * 12) : 0
            ])
          )
        },
        forecast: {
          nextMonth: recurring,
          nextQuarter: recurring * 3,
          confidence: activeSubscribers > 0 ? 0.6 : 0
        },
        paymentMethods: {}
      }
    } catch (error) {
      logger.error('[Analytics] Failed to compute revenue analytics', { error })
      return this.emptyRevenueAnalytics(period)
    }
  }

  /**
   * Get feature analytics
   */
  async getFeatureAnalytics(period: 'day' | 'week' | 'month' | 'quarter' | 'year'): Promise<FeatureAnalytics[]> {
    if (this.useMocks()) return this.mockFeatureAnalytics()

    const { startDate, endDate } = this.getDateRange(period)

    try {
      const usersSnapshot = await db.collection('users').get()
      const totalUsers = Math.max(usersSnapshot.size, 1)
      const results: FeatureAnalytics[] = []

      for (const feature of DEFAULT_FEATURES) {
        const eventsSnapshot = await db
          .collection('analytics_events')
          .where('event', '==', 'feature_used')
          .where('properties.feature', '==', feature)
          .where('timestamp', '>=', startDate)
          .where('timestamp', '<=', endDate)
          .get()

        const uniqueUsers = new Set<string>()
        let totalUsage = 0
        let totalLoadTime = 0
        let successCount = 0
        let errorCount = 0

        for (const doc of eventsSnapshot.docs) {
          const data = doc.data() as Record<string, any>
          totalUsage += 1
          const userId = data.userId || data.uid || data.user
          if (userId) uniqueUsers.add(String(userId))
          const loadTime =
            typeof data.performance?.durationMs === 'number'
              ? data.performance.durationMs
              : typeof data.duration === 'number'
                ? data.duration
                : typeof data.latencyMs === 'number'
                  ? data.latencyMs
                  : null
          if (typeof loadTime === 'number') {
            totalLoadTime += loadTime
          }
          const status = typeof data.status === 'number' ? data.status : null
          const successFlag = typeof data.success === 'boolean' ? data.success : status ? status < 400 : true
          if (successFlag) {
            successCount += 1
          } else {
            errorCount += 1
          }
        }

        const averagePerUser = uniqueUsers.size ? totalUsage / uniqueUsers.size : 0
        const adoptionRate = Math.round((uniqueUsers.size / totalUsers) * 10_000) / 100
        const averageLoadTime = totalUsage ? Math.round(totalLoadTime / totalUsage) : 0
        const errorRate = totalUsage ? Math.round((errorCount / totalUsage) * 10_000) / 100 : 0
        const successRate = totalUsage ? Math.round((successCount / totalUsage) * 10_000) / 100 : 100

        results.push({
          feature,
          usage: {
            total: totalUsage,
            unique: uniqueUsers.size,
            averagePerUser
          },
          engagement: {
            activeUsers: uniqueUsers.size,
            adoptionRate: Number.isFinite(adoptionRate) ? adoptionRate : 0,
            stickiness: totalUsers ? Math.round((uniqueUsers.size / totalUsers) * 1000) / 1000 : 0
          },
          performance: {
            averageLoadTime,
            errorRate,
            successRate
          },
          trend: {
            current: totalUsage,
            previous: 0,
            change: 0
          },
          topUsers: []
        })
      }

      return results
    } catch (error) {
      logger.error('[Analytics] Failed to compute feature analytics', { error })
      return this.emptyFeatureAnalytics()
    }
  }

  /**
   * Get AI usage analytics
   */
  async getAIUsageAnalytics(period: 'day' | 'week' | 'month'): Promise<AIUsageAnalytics> {
    if (this.useMocks()) return this.mockAIUsageAnalytics(period)

    const { startDate, endDate } = this.getDateRange(period)

    try {
      const eventsSnapshot = await db
        .collection('analytics_events')
        .where('event', '==', 'ai_request')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()

      const byModel: AIUsageAnalytics['byModel'] = {}
      const byFeature: AIUsageAnalytics['byFeature'] = {}
      let totalRequests = 0
      let totalTokens = 0
      let totalCost = 0
      let totalLatency = 0
      let errorCount = 0
      let retryCount = 0

      for (const doc of eventsSnapshot.docs) {
        const data = doc.data() as Record<string, any>
        totalRequests += 1
        const model = typeof data.model === 'string' ? data.model : data.properties?.model
        const feature = data.feature || data.properties?.feature || 'unknown'
        const tokens = Number(data.tokens || data.properties?.tokens || 0)
        const cost = Number(data.cost || data.properties?.cost || 0)
        const latency = Number(data.durationMs || data.properties?.durationMs || 0)
        const status = typeof data.status === 'number' ? data.status : null
        const retries = Number(data.retries || data.properties?.retries || 0)

        if (Number.isFinite(tokens)) totalTokens += tokens
        if (Number.isFinite(cost)) totalCost += cost
        if (Number.isFinite(latency)) totalLatency += latency
        if (Number.isFinite(retries)) retryCount += retries
        if (status && status >= 400) errorCount += 1

        if (model) {
          if (!byModel[model]) {
            byModel[model] = { requests: 0, tokens: 0, cost: 0, averageTokensPerRequest: 0 }
          }
          byModel[model].requests += 1
          byModel[model].tokens += Number.isFinite(tokens) ? tokens : 0
          byModel[model].cost += Number.isFinite(cost) ? cost : 0
        }

        if (!byFeature[feature]) {
          byFeature[feature] = { requests: 0, tokens: 0, cost: 0 }
        }
        byFeature[feature].requests += 1
        byFeature[feature].tokens += Number.isFinite(tokens) ? tokens : 0
        byFeature[feature].cost += Number.isFinite(cost) ? cost : 0
      }

      for (const model of Object.keys(byModel)) {
        const modelStats = byModel[model]
        modelStats.averageTokensPerRequest = modelStats.requests
          ? Math.round(modelStats.tokens / modelStats.requests)
          : 0
      }

      return {
        period,
        totalRequests,
        totalTokens,
        totalCost,
        byModel,
        byFeature,
        performance: {
          averageResponseTime: totalRequests ? Math.round(totalLatency / totalRequests) : 0,
          errorRate: totalRequests ? Math.round((errorCount / totalRequests) * 10_000) / 100 : 0,
          retryRate: totalRequests ? Math.round((retryCount / totalRequests) * 10_000) / 100 : 0
        },
        quality: {
          userSatisfaction: undefined,
          thumbsUp: 0,
          thumbsDown: 0
        },
        topPrompts: []
      }
    } catch (error) {
      logger.error('[Analytics] Failed to compute AI usage analytics', { error })
      return this.emptyAIUsageAnalytics(period)
    }
  }

  /**
   * Get system analytics
   */
  async getSystemAnalytics(period: 'day' | 'week' | 'month'): Promise<SystemAnalytics> {
    if (this.useMocks()) return this.mockSystemAnalytics(period)

    const { startDate, endDate } = this.getDateRange(period)

    try {
      const apiRequestsSnapshot = await db
        .collection('analytics_events')
        .where('event', '==', 'api_request')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()

      const httpRequestsSnapshot = await db
        .collection('analytics_events')
        .where('event', '==', 'http_request')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()

      const requestsDocs = [...apiRequestsSnapshot.docs, ...httpRequestsSnapshot.docs]

      const byEndpoint: SystemAnalytics['requests']['byEndpoint'] = {}
      const errorTypes: Record<string, number> = {}
      const topErrorsMap = new Map<string, { count: number; lastOccurrence: Date }>()

      let totalRequests = 0
      let successful = 0
      let failed = 0
      let totalLatency = 0
      const latencies: number[] = []

      for (const doc of requestsDocs) {
        const data = doc.data() as Record<string, any>
        totalRequests += 1
        const status = typeof data.status === 'number' ? data.status : 200
        const path = typeof data.path === 'string' ? data.path : data.endpoint || 'unknown'
        const latency = Number(data.durationMs || data.latencyMs || data.responseTimeMs || 0)
        if (Number.isFinite(latency)) {
          totalLatency += latency
          latencies.push(latency)
        }

        if (!byEndpoint[path]) {
          byEndpoint[path] = { count: 0, averageResponseTime: 0, errorRate: 0 }
        }
        byEndpoint[path].count += 1

        if (status >= 400) {
          failed += 1
          errorTypes[String(status)] = (errorTypes[String(status)] ?? 0) + 1
          const existing = topErrorsMap.get(String(status)) ?? { count: 0, lastOccurrence: new Date(0) }
          const timestamp = data.timestamp ? new Date(data.timestamp) : new Date()
          topErrorsMap.set(String(status), {
            count: existing.count + 1,
            lastOccurrence: timestamp > existing.lastOccurrence ? timestamp : existing.lastOccurrence
          })
        } else {
          successful += 1
        }
      }

      for (const endpoint of Object.keys(byEndpoint)) {
        const stats = byEndpoint[endpoint]
        stats.averageResponseTime = stats.count
          ? Math.round(totalLatency / Math.max(totalRequests, 1))
          : 0
        stats.errorRate = stats.count ? Math.round(((failed / stats.count) || 0) * 10_000) / 100 : 0
      }

      const availability = totalRequests
        ? Math.round(((totalRequests - failed) / totalRequests) * 10_000) / 100
        : 0

      const sortedLatencies = latencies.sort((a, b) => a - b)
      const percentile = (p: number) => {
        if (!sortedLatencies.length) return 0
        const idx = Math.min(sortedLatencies.length - 1, Math.floor((p / 100) * sortedLatencies.length))
        return sortedLatencies[idx]
      }

      const topErrors = Array.from(topErrorsMap.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5)
        .map(([error, meta]) => ({
          error,
          count: meta.count,
          lastOccurrence: meta.lastOccurrence
        }))

      return {
        period,
        availability,
        performance: {
          averageResponseTime: totalRequests ? Math.round(totalLatency / totalRequests) : 0,
          p50: percentile(50),
          p95: percentile(95),
          p99: percentile(99)
        },
        errors: {
          total: failed,
          rate: totalRequests ? Math.round((failed / totalRequests) * 10_000) / 100 : 0,
          byType: errorTypes,
          topErrors
        },
        requests: {
          total: totalRequests,
          successful,
          failed,
          byEndpoint
        },
        cache: {
          hitRate: 0,
          hits: 0,
          misses: 0,
          evictions: 0
        },
        database: {
          reads: 0,
          writes: 0,
          deletes: 0,
          averageQueryTime: 0
        }
      }
    } catch (error) {
      logger.error('[Analytics] Failed to compute system analytics', { error })
      return this.emptySystemAnalytics(period)
    }
  }

  /**
   * Generate insights and recommendations
   */
  async generateInsights(data: {
    users: UserAnalytics
    revenue: RevenueAnalytics
    features: FeatureAnalytics[]
    aiUsage: AIUsageAnalytics
    system: SystemAnalytics
  }): Promise<InsightRecommendation[]> {
    const insights: InsightRecommendation[] = []

    // High churn rate
    if (data.revenue.churn.rate > 5) {
      insights.push({
        id: 'churn-alert',
        type: 'risk',
        priority: 'high',
        title: 'High churn rate detected',
        description: `Churn rate is ${data.revenue.churn.rate.toFixed(1)}%, above healthy threshold of 5%`,
        impact: {
          metric: 'revenue',
          estimated: data.revenue.churn.revenue,
          unit: '$'
        },
        confidence: 0.9,
        evidence: [
          `${data.revenue.churn.count} customers churned this period`,
          `Lost ${(data.revenue.churn.revenue / 100).toFixed(2)}$ in revenue`
        ],
        actionable: true,
        action: {
          type: 'retention-campaign',
          description: 'Launch win-back campaign with special offers',
          effort: 'medium'
        },
        createdAt: new Date()
      })
    }

    // Low feature adoption
    const lowAdoptionFeatures = data.features.filter(f => f.engagement.adoptionRate < 30)
    if (lowAdoptionFeatures.length > 0) {
      insights.push({
        id: 'feature-adoption',
        type: 'opportunity',
        priority: 'medium',
        title: 'Low feature adoption opportunity',
        description: `${lowAdoptionFeatures.length} features have <30% adoption rate`,
        impact: {
          metric: 'engagement',
          estimated: 25,
          unit: '%'
        },
        confidence: 0.75,
        evidence: lowAdoptionFeatures.map(f => `${f.feature}: ${f.engagement.adoptionRate.toFixed(0)}% adoption`),
        actionable: true,
        action: {
          type: 'onboarding-improvement',
          description: 'Improve onboarding to highlight these features',
          effort: 'medium'
        },
        relatedMetrics: lowAdoptionFeatures.map(f => f.feature),
        createdAt: new Date()
      })
    }

    // High AI costs
    if (data.aiUsage.totalCost > 5000) { // >$50
      insights.push({
        id: 'ai-cost',
        type: 'optimization',
        priority: 'high',
        title: 'AI costs optimization opportunity',
        description: `AI costs at ${(data.aiUsage.totalCost / 100).toFixed(2)}$/period, consider caching strategies`,
        impact: {
          metric: 'cost',
          estimated: data.aiUsage.totalCost * 0.3,
          unit: '$'
        },
        confidence: 0.85,
        evidence: [
          `${data.aiUsage.totalRequests} AI requests`,
          `${(data.aiUsage.totalTokens / 1_000_000).toFixed(2)}M tokens used`
        ],
        actionable: true,
        action: {
          type: 'enable-caching',
          description: 'Implement response caching for similar queries',
          effort: 'low'
        },
        createdAt: new Date()
      })
    }

    return insights
  }

  /**
   * Helper: Get date range for period
   */
  private getDateRange(period: 'day' | 'week' | 'month' | 'quarter' | 'year'): {
    startDate: Date
    endDate: Date
  } {
    const now = new Date()
    let startDate: Date

    switch (period) {
      case 'day':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      case 'quarter':
        startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
        break
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1)
        break
    }

    return { startDate, endDate: now }
  }
}

export const analyticsService = new AnalyticsService()
